import React, { useState, useRef, useCallback, useEffect } from 'react';
import Uploady, { withRequestPreSendUpdate, UPLOADER_EVENTS } from '@rpldy/uploady';
import UploadButton from '@rpldy/upload-button';
import UploadPreview, { PREVIEW_TYPES } from '@rpldy/upload-preview';
import { Button as ButtonComponent, Typography } from '@material-ui/core';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import PropTypes from 'prop-types';
import 'react-image-crop/dist/ReactCrop.css';
import Drawer from 'sharedComponents/Drawer';
import cropImage from './cropImage';

const ItemPreviewWithCrop = withRequestPreSendUpdate((props) => {
  const {
    url,
    isFallback,
    type,
    updateRequest,
    requestData,
    previewMethods,
    withCircleSelection,
    aspectRatio,
    classes,
    setProfileImage,
    picType,
    onUpdate,
  } = props;

  const cropRef = useRef(null);
  const [isValidFile, setIsValidFile] = useState(true);
  const [crop, setCrop] = useState();
  const [croppedUrl, setCroppedUrl] = useState(null);

  const onUploadCrop = useCallback(async () => {
    if (updateRequest && (crop?.height || crop?.width)) {
      const {
        blob: croppedBlob,
        blobUrl,
        revokeUrl,
      } = await cropImage(cropRef.current, requestData.items[0].file, crop, true);

      requestData.items[0].file = croppedBlob;

      updateRequest({ items: requestData.items });

      await setProfileImage(picType, requestData.items[0].file);

      setCroppedUrl({ blobUrl, revokeUrl });
      onUpdate();
    }
  }, [requestData, updateRequest, crop]);

  const onUploadCancel = useCallback(() => {
    updateRequest(false);
    if (previewMethods.current?.clear) {
      previewMethods.current.clear();
    }
  }, [updateRequest, previewMethods]);

  const getMeta = (url, cb) => {
    const imgTemp = new Image();
    imgTemp.onload = () => cb(null, imgTemp);
    imgTemp.onerror = (err) => cb(err);
    imgTemp.src = url;
  };

  useEffect(() => {
    getMeta(url, (err) => {
      if (err) setIsValidFile(false);
    });
    return () => {};
  }, [url]);

  useEffect(() => () => croppedUrl?.revokeUrl(), [croppedUrl]);

  const centerAspectCrop = (mediaWidth, mediaHeight, aspect) => {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: withCircleSelection ? 40 : 80,
        },
        aspect,
        mediaWidth,
        mediaHeight,
      ),
      mediaWidth,
      mediaHeight,
    );
  };

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspectRatio || 3 / 2));
  }

  return !isValidFile || isFallback || type !== PREVIEW_TYPES.IMAGE ? (
    <div className={classes.imageWarning}>
      <Typography variant="h4">Sorry, this is an unsupported image type</Typography>
      <ButtonComponent
        variant="contained"
        disableElevation
        color="primary"
        className={classes.uploadPicButton}
        onClick={onUploadCancel}
      >
        Back
      </ButtonComponent>
    </div>
  ) : (
    <>
      <div className={classes.cropContainer}>
        <Typography variant="h2" className={classes.title}>
          Crop
        </Typography>
        {requestData ? (
          <ReactCrop
            crop={crop}
            onChange={setCrop}
            onComplete={setCrop}
            circularCrop={withCircleSelection}
            aspect={aspectRatio}
            minWidth={150}
            minHeight={150}
          >
            <img src={url} alt="avatar" ref={cropRef} onLoad={onImageLoad} />
          </ReactCrop>
        ) : (
          <img src={croppedUrl?.blobUrl || url} alt="img to upload" />
        )}
        <div className={classes.cropButtons}>
          <button type="button" style={{ display: updateRequest && crop ? 'block' : 'none' }} onClick={onUploadCancel}>
            Cancel
          </button>
          <button type="button" style={{ display: updateRequest && crop ? 'block' : 'none' }} onClick={onUploadCrop}>
            Choose
          </button>
        </div>
      </div>
    </>
  );
});

const ImageUploader = ({
  extraClasses,
  classes,
  setUploadError,
  withCircleSelection,
  aspectRatio,
  setProfileImage,
  picType,
  onClose,
  onUpdate,
}) => {
  const previewMethodsRef = useRef();
  const [drawer, setDrawer] = React.useState({ open: false });

  const onCloseDrawer = () => {
    setDrawer({ open: false, title: '', content: null });
  };

  const onOpenDrawer = () => {
    setDrawer({
      open: true,
      title: 'Crop',
      content: (
        <UploadPreview
          PreviewComponent={ItemPreviewWithCrop}
          previewComponentProps={{
            previewMethods: previewMethodsRef,
            classes,
            withCircleSelection,
            aspectRatio,
            setProfileImage,
            picType,
            onClose,
            onUpdate,
          }}
          previewMethodsRef={previewMethodsRef}
        />
      ),
    });
  };

  const listeners = {
    [UPLOADER_EVENTS.BATCH_ADD]: () => {
      onOpenDrawer();
    },
    [UPLOADER_EVENTS.BATCH_CANCEL]: () => {
      onCloseDrawer();
    },
    [UPLOADER_EVENTS.BATCH_FINISH]: () => {
      onCloseDrawer();
    },
  };

  const fileFilter = useCallback((file) => {
    const isFileCorrect = file.size < 8388608;
    setUploadError(!isFileCorrect);
    return isFileCorrect;
  }, []);

  return (
    <Uploady fileFilter={fileFilter} multiple={false} listeners={listeners} accept="image/png, image/jpeg">
      <UploadButton text="Upload Picture" className={extraClasses.uploadPicButton} />
      <Drawer fullWidth open={drawer.open} onClose={onCloseDrawer}>
        {drawer.open && drawer.content}
      </Drawer>
    </Uploady>
  );
};

ImageUploader.propTypes = {
  extraClasses: PropTypes.objectOf(PropTypes.string),
  classes: PropTypes.objectOf(PropTypes.string),
  setUploadError: PropTypes.func,
  withCircleSelection: PropTypes.bool,
  aspectRatio: PropTypes.number,
};

ImageUploader.defaultProps = {
  classes: {},
  withCircleSelection: false,
};
export default ImageUploader;
